'use client';

import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useForm } from 'react-hook-form';
import { FaLongArrowAltRight } from "react-icons/fa";
import ThemedSkeleton from "../../ui/ThemedSkeleton";

import { useGetBusinessProfile } from '../business_profile/useGetBusinessProfile';
import { useUpdateBusinessProfile } from '../business_profile/useUpdateBusinessProfile';

export function OnboardingForm({ onStepComplete }) {
  const [pending, setPending] = useState(false);

  const { data: profile, isLoading: isLoadingProfile } = useGetBusinessProfile();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const { mutateAsync: updateBusinessProfile, isLoading: isUpdatingProfile } = useUpdateBusinessProfile();

  useEffect(() => {
    if (profile) {
      setValue('company_name', profile.company_name);
      setValue('company_address', profile.company_address);
      setValue('uei', profile.uei);
      setValue('cage_code', profile.cage_code);
      setValue('locations_description', profile.locations_description);
    }
  }, [profile, setValue]);

  async function onSubmit(formData) {
    setPending(true);
    try {
      const result = await updateBusinessProfile(formData);
      if (result) {
        onStepComplete();
      } else {
        console.error("Profile update failed:", result);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
    } finally {
      setPending(false);
    }
  }

  if (isLoadingProfile) {
    return (
      <div className="flex flex-col gap-5">
        <ThemedSkeleton className="h-10 w-5/6" />
      </div>
    );
  }

  return (
    <Card className="w-full max-w-4xl bg-white text-black shadow-md rounded-lg">
      <CardContent className="p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <h1 className="text-2xl font-semibold mb-2">Business Information</h1>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="company_name">Legal Business Name*</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  placeholder="e.g., ABC Technologies Inc."
                  className="bg-transparent placeholder-gray-500"
                  {...register('company_name', {
                    required: "Please enter your legal business name.",
                  })}
                />
                {errors.company_name && <p className="text-red-500 text-sm">{errors.company_name.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="company_address">Incorporated Address*</Label>
                <Input
                  id="company_address"
                  name="company_address"
                  placeholder="e.g., ABC Technologies Inc.
                  4567 Innovation Drive, Suite 890
                  San Francisco, CA 94105
                  United States"
                  className="bg-transparent placeholder-gray-500"
                  {...register('company_address', {
                    required: "Please provide your business address.",
                  })}
                />
                {errors.company_address && <p className="text-red-500 text-sm">{errors.company_address.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="uei">Unique Entity Identifier (UEI)</Label>
                <Input
                  id="uei"
                  name="uei"
                  placeholder="e.g., ABC123DEF456"
                  className="bg-transparent placeholder-gray-500"
                  {...register('uei')}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="locations_description">Operating Region(s)</Label>
                <Input
                  id="locations_description"
                  name="locations_description"
                  placeholder="e.g., Atlanta, GA; New York, NY"
                  className="bg-transparent placeholder-gray-500"
                  {...register('locations_description')}
                />
                {errors.locations_description && <p className="text-red-500 text-sm">{errors.locations_description.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="cage_code">CAGE Code</Label>
                <Input
                  id="cage_code"
                  name="cage_code"
                  placeholder="e.g., 1A2B3"
                  className="bg-transparent placeholder-gray-500"
                  {...register('cage_code')}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="terms">
                <input
                  type="checkbox"
                  id="terms"
                  {...register('terms', {
                    required: "You must agree to the terms and conditions.",
                  })}
                />
                <span className="ml-2">I agree to the terms and conditions*</span>
              </Label>
              {errors.terms && (
                <p className="text-red-500 text-sm">{errors.terms.message}</p>
              )}
            </div>

          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={pending || isUpdatingProfile}>
              {pending || isUpdatingProfile ? "Saving..." : "Save and Continue"}
              {!pending && !isUpdatingProfile && <FaLongArrowAltRight className="ml-2" />}
            </Button>
          </div>
        </form>

        {/* Terms and Conditions Section */}
        <div className="flex flex-row justify-center mt-4 space-x-2">
          <a
            href="https://samsearch.co/terms-of-use"
            target="_blank"
            className="text-xs text-gray-600 hover:text-blue-400"
            rel="noreferrer"
          >
            Terms and Conditions
          </a>
          <span className="text-xs text-gray-500">|</span>
          <a
            href="https://samsearch.co/privacy-policy"
            target="_blank"
            className="text-xs text-gray-600 hover:text-blue-400"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
        </div>
      </CardContent>
    </Card>
  );
}

export default OnboardingForm;
